import { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import WarningPage from './WarningPage';
import RetryNotice, { RetryInline } from './RetryNotice';
import { departmentLabel } from './departments';
import { UpdateButton } from '../../components/mui/Buttons/Buttons';
import apiRequest from '../../api/request';
import useWarningMonth from './useWarningMonth';
import WorkingMonthBar from './WorkingMonthBar';
import EffectiveFromField from './EffectiveFromField';
import AppliesCell from './AppliesCell';
import ActiveSwitch from './ActiveSwitch';
import { defaultEffectiveMonth, emptyEffective } from './effectiveWindow';
import { slabFieldProps, validateSlab } from './thresholdValidation';
import MetricDefinitionNote from './MetricDefinitionNote';
import TargetSlabBulkUpload from './TargetSlabBulkUpload';
import useConfigAccess from './useConfigAccess';

const DEPARTMENTS = [
  { id: 11, name: 'OGD' },
  { id: 12, name: 'CSD' },
  { id: 3, name: 'Foundation' },
  { id: 20, name: 'Classroom' },
  { id: 21, name: 'Little Genie' },
  { id: 22, name: 'Speak Pro' },
  { id: 35, name: 'SkillX' },
];
const departmentName = (id) => departmentLabel(id, DEPARTMENTS);

const emptyForm = {
  department_id: '',
  target_min: '',
  target_max: '',
  tar_threshold: '',
  arpu_threshold: '',
  admission_count_threshold: '',
  lcrr_threshold: '',
  is_active: true,
};

// Same shape as emptyForm, but `department_ids` (plural, multi-select) instead of one
// `department_id` - used only for the ADD flow (see draftRows below). Editing an existing slab
// stays single-row/single-department (emptyForm/`form`) since an edit is inherently about one
// already-saved row, not a batch of new ones.
const emptyDraftRow = {
  department_ids: [],
  target_min: '',
  target_max: '',
  tar_threshold: '',
  arpu_threshold: '',
  admission_count_threshold: '',
  lcrr_threshold: '',
  is_active: true,
};

const formatRange = (min, max) => `₹${Number(min).toLocaleString()} - ${max === null ? 'and above' : `₹${Number(max).toLocaleString()}`}`;

// One shared config screen for TAR + ARPU + Admission Count + LCRR
// (Requirement Document v1.0 §3 "Target Slab" model) - Finance sets a
// department's target ranges once, and all four metrics' dashboards read
// their own threshold column off the same slab row (see
// targetSlab.shared.js/target_slab_config.controller.js). Not four
// separate config screens, since that would ask Finance to configure the
// same ranges four times. lcrr_threshold added 2026-09-30. Add/edit is an
// inline panel on the page, not a popup - with 7 fields, a dialog box
// felt cramped; laying the four threshold fields out two-per-row inline
// reads easier.
const TargetSlabConfig = () => {
  const [month, setMonth] = useWarningMonth(); // the month being worked on - passed on to the results screens
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  // One \"Applies from\" choice for the whole Add (every row/department in it) - edits carry their own in `form`.
  const [draftEffective, setDraftEffective] = useState(emptyEffective);
  const [draftRows, setDraftRows] = useState([emptyDraftRow]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // blank slab fields only complain after the first Save attempt
  const [submitted, setSubmitted] = useState(false);

  const fetchSeq = useRef(0); // each load gets a number; only the newest may update the screen (fast clicks must not show an older answer)
  const fetchRows = ({ silent = false } = {}) => {
    const seq = ++fetchSeq.current;
    if (!silent) setRowsLoading(true); // a silent reload (after a switch) keeps the table on screen - no flash
    // apiRequest resolves even on a failed call (src/api/request.js) - this status check is required, a
    // .catch alone would never fire and a server error would silently render as "no slabs configured".
    return apiRequest({ url: `warning-dashboard/target-slabs?month=${month.format('YYYY-MM-DD')}`, method: 'get' })
      .then((res) => {
        if (seq !== fetchSeq.current) return; // a newer load replaced this one
        if (res?.status !== 'success') {
          setRowsError(res?.message || 'Could not load target slabs');
          return;
        }
        setRows(res?.data?.rows || []);
        setRowsError(null);
      })
      .catch((err) => {
        if (seq === fetchSeq.current) setRowsError(err?.response?.data?.message || 'Could not load target slabs');
      })
      .finally(() => {
        if (seq === fetchSeq.current) setRowsLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
  }, [month]); // reload when the working month changes

  const openCreate = () => {
    setEditingId(null);
    setDraftEffective(emptyEffective()); // read fresh each time the form opens
    setDraftRows([{ ...emptyDraftRow, department_ids: defaultDepartment(DEPARTMENTS) === '' ? [] : [defaultDepartment(DEPARTMENTS)] }]);
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  // New row starts with the PREVIOUS row's department selection carried over (not blank) - the
  // common case is adding another RANGE for the same department(s), e.g. a second slab for OGD,
  // not picking departments from scratch every time.
  const addDraftRow = () => {
    setDraftRows((prev) => [...prev, { ...emptyDraftRow, department_ids: prev[prev.length - 1]?.department_ids || [] }]);
  };

  const removeDraftRow = (index) => {
    setDraftRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const updateDraftRow = (index, field, value) => {
    setDraftRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      department_id: row.department_id,
      target_min: row.target_min,
      target_max: row.target_max === null ? '' : row.target_max,
      tar_threshold: row.tar_threshold,
      arpu_threshold: row.arpu_threshold,
      admission_count_threshold: row.admission_count_threshold,
      lcrr_threshold: row.lcrr_threshold,
      is_active: true, // opening an edit on a switched-off threshold means "use it" - switch it off again below if not
      ...emptyEffective(),
    });
    setSaveError(null);
    setSubmitted(false);
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  const handleSave = () => {
    // BUG-03: blank, negative, TAR/LCRR over 100%, a fractional Admission Count, or 'up to' not above
    // 'from' are all caught here (the server enforces the same rules)
    setSubmitted(true);
    if (!form.department_id || Object.keys(validateSlab(form)).length) return;
    setSaving(true);
    const request = editingId
      ? apiRequest({ url: `warning-dashboard/target-slabs/${editingId}`, method: 'put', data: form })
      : apiRequest({ url: 'warning-dashboard/target-slabs', method: 'post', data: form });

    request
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save this slab');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save this slab'))
      .finally(() => setSaving(false));
  };

  // Each draft row can target SEVERAL departments at once (department_ids, plural) - expand
  // every row into one entry per selected department before sending, since the bulk endpoint
  // (and the underlying table) is still one row per department, same as the regular single-save
  // path. Reuses the exact bulk endpoint TargetSlabBulkUpload's CSV flow already uses - same
  // server-side validation (every row checked, overlaps checked both within this batch and
  // against the DB, all-or-nothing in one transaction).
  const handleSaveDrafts = () => {
    setSubmitted(true);
    if (draftRows.some((row) => Object.keys(validateSlab(row)).length)) {
      setSaveError('Fix the highlighted fields before saving.');
      return;
    }
    const rowMissingDept = draftRows.some((row) => row.department_ids.length === 0);
    if (rowMissingDept) {
      setSaveError('Every row needs at least one department selected.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    const entries = draftRows.flatMap((row) =>
      row.department_ids.map((departmentId) => ({
        department_id: departmentId,
        target_min: row.target_min,
        target_max: row.target_max,
        tar_threshold: row.tar_threshold,
        arpu_threshold: row.arpu_threshold,
        admission_count_threshold: row.admission_count_threshold,
        lcrr_threshold: row.lcrr_threshold,
        is_active: row.is_active,
      }))
    );

    apiRequest({ url: 'warning-dashboard/target-slabs/bulk', method: 'post', data: { entries, effective_month: draftEffective.effective_month, effective_until: draftEffective.effective_until, only_this_month: draftEffective.only_this_month } })
      .then((res) => {
        if (res?.status !== 'success') {
          setSaveError(res?.message || 'Could not save these slabs');
          return;
        }
        setFormOpen(false);
        return fetchRows();
      })
      .catch((err) => setSaveError(err?.response?.data?.message || 'Could not save these slabs'))
      .finally(() => setSaving(false));
  };

  const [toggling, setToggling] = useState({}); // rows whose switch request is still in flight - one at a time per row
  const handleToggleActive = (row) => {
    if (toggling[row.id]) return;
    setToggling((t) => ({ ...t, [row.id]: true }));
    const nextActive = !row.is_active;
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: nextActive } : r)));
    apiRequest({ url: `warning-dashboard/target-slabs/${row.id}/active`, method: 'patch', data: { is_active: nextActive, effective_month: defaultEffectiveMonth() } })
      .then((res) => {
        if (res?.status !== 'success') {
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
        } else {
          fetchRows({ silent: true }); // a toggle can start a new version from this month
        }
      })
      .catch(() => {
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, is_active: row.is_active } : r)));
      })
      .finally(() => setToggling((t) => { const next = { ...t }; delete next[row.id]; return next; }));
  };

  const groupedByDepartment = useMemo(() => {
    const groups = new Map();
    rows.forEach((r) => {
      if (!groups.has(r.department_id)) groups.set(r.department_id, []);
      groups.get(r.department_id).push(r);
    });
    groups.forEach((list) => list.sort((a, b) => Number(a.target_min) - Number(b.target_min) || String(a.effective_from).localeCompare(String(b.effective_from))));
    return groups;
  }, [rows]);

  const { defaultDepartment } = useConfigAccess(); // an Add form starts on the person's own department (they can change it)

  return (
    <WarningPage
      title="Target Slab Configuration"
      actions={
        <Box display="flex" gap={1}>
          <Button component={Link} to={`/dashboard/warning-dashboard/tar?month=${month.format('YYYY-MM-DD')}`} variant="outlined">TAR</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/arpu?month=${month.format('YYYY-MM-DD')}`} variant="outlined">ARPU</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/admission-count?month=${month.format('YYYY-MM-DD')}`} variant="outlined">Admission Count</Button>
          <Button component={Link} to={`/dashboard/warning-dashboard/lcrr?month=${month.format('YYYY-MM-DD')}`} variant="outlined">LCRR</Button>
          <TargetSlabBulkUpload rows={rows} disabled={rowsLoading} onSaved={fetchRows} />
          {!formOpen && (
            <Button startIcon={<Icon icon="mynaui:config-solid" width="20" />} variant="contained" onClick={openCreate}>
              Add Slab
            </Button>
          )}
        </Box>
      }
    >
      <WorkingMonthBar month={month} setMonth={setMonth} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        An employee's own Target Revenue for the period picks which slab
        applies within their department; that slab's four thresholds drive
        TAR, ARPU, Admission Count, and LCRR warnings together. Ranges must
        not overlap within a department, and every employee's target should
        fall inside some slab - a target outside all configured ranges
        shows as "Needs Slab Configuration" on those dashboards instead of
        silently passing.
      </Typography>

      {formOpen && editingId && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Edit Target Slab
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <MetricDefinitionNote items={['tar', 'arpu', 'admission_count', 'lcrr']} />
            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                select
                label="Department"
                value={form.department_id}
                onChange={(e) => setForm({ ...form, department_id: e.target.value })}
                sx={{ minWidth: 200, flex: 1 }}
              >
                {DEPARTMENTS.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Target Revenue from"
                value={form.target_min}
                onChange={(e) => setForm({ ...form, target_min: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...slabFieldProps('target_min', form, submitted)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <TextField
                label="up to"
                value={form.target_max}
                onChange={(e) => setForm({ ...form, target_max: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...slabFieldProps('target_max', form, submitted, 'Blank = no upper bound')}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap">
              <TextField
                label="TAR warns below"
                value={form.tar_threshold}
                onChange={(e) => setForm({ ...form, tar_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...slabFieldProps('tar_threshold', form, submitted)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
              <TextField
                label="ARPU warns below"
                value={form.arpu_threshold}
                onChange={(e) => setForm({ ...form, arpu_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...slabFieldProps('arpu_threshold', form, submitted)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <TextField
                label="Admission Count warns below"
                value={form.admission_count_threshold}
                onChange={(e) => setForm({ ...form, admission_count_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...slabFieldProps('admission_count_threshold', form, submitted)}
              />
              <TextField
                label="LCRR warns below"
                value={form.lcrr_threshold}
                onChange={(e) => setForm({ ...form, lcrr_threshold: e.target.value })}
                sx={{ minWidth: 180, flex: 1 }}
                {...slabFieldProps('lcrr_threshold', form, submitted)}
              />
            </Box>

            <EffectiveFromField form={form} setForm={setForm} />

            <ActiveSwitch form={form} setForm={setForm} offHint="While off, this slab is ignored - people whose target falls in it have no slab." />

            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</UpdateButton>
              <Button variant="text" onClick={closeForm} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {formOpen && !editingId && (
        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Add Target Slab{draftRows.length > 1 ? 's' : ''}
          </Typography>

          <Box display="flex" flexDirection="column" gap={2.5}>
            <MetricDefinitionNote items={['tar', 'arpu', 'admission_count', 'lcrr']} />
            <Typography variant="body2" color="text.secondary">
              Select more than one department on a row to create the same slab for all of them at once. Use
              "Add Another Row" to queue up several ranges (same department or different ones) before saving -
              everything below is saved together in one go.
            </Typography>

            {draftRows.map((row, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2, position: 'relative', bgcolor: 'grey.50' }}>
                {draftRows.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeDraftRow(index)}
                    sx={{ position: 'absolute', top: 4, right: 4 }}
                    aria-label="Remove this row"
                  >
                    <Icon icon="mdi:close" width={18} />
                  </IconButton>
                )}

                <Box display="flex" gap={2} flexWrap="wrap">
                  <TextField
                    select
                    label="Departments"
                    value={row.department_ids}
                    onChange={(e) => updateDraftRow(index, 'department_ids', e.target.value)}
                    sx={{ minWidth: 220, flex: 1 }}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => selected.map((id) => departmentName(id)).join(', ') || 'Select...',
                    }}
                  >
                    {DEPARTMENTS.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        <Checkbox checked={row.department_ids.includes(d.id)} size="small" />
                        <ListItemText primary={d.name} />
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Target Revenue from"
                    value={row.target_min}
                    onChange={(e) => updateDraftRow(index, 'target_min', e.target.value)}
                    sx={{ minWidth: 160, flex: 1 }}
                    {...slabFieldProps('target_min', row, submitted)}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  />
                  <TextField
                    label="up to"
                    value={row.target_max}
                    onChange={(e) => updateDraftRow(index, 'target_max', e.target.value)}
                    sx={{ minWidth: 160, flex: 1 }}
                    {...slabFieldProps('target_max', row, submitted, 'Blank = no upper bound')}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  />
                </Box>

                <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                  <TextField
                    label="TAR warns below"
                    value={row.tar_threshold}
                    onChange={(e) => updateDraftRow(index, 'tar_threshold', e.target.value)}
                    sx={{ minWidth: 160, flex: 1 }}
                    {...slabFieldProps('tar_threshold', row, submitted)}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                  <TextField
                    label="ARPU warns below"
                    value={row.arpu_threshold}
                    onChange={(e) => updateDraftRow(index, 'arpu_threshold', e.target.value)}
                    sx={{ minWidth: 160, flex: 1 }}
                    {...slabFieldProps('arpu_threshold', row, submitted)}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  />
                  <TextField
                    label="Admission Count warns below"
                    value={row.admission_count_threshold}
                    onChange={(e) => updateDraftRow(index, 'admission_count_threshold', e.target.value)}
                    sx={{ minWidth: 160, flex: 1 }}
                    {...slabFieldProps('admission_count_threshold', row, submitted)}
                  />
                  <TextField
                    label="LCRR warns below"
                    value={row.lcrr_threshold}
                    onChange={(e) => updateDraftRow(index, 'lcrr_threshold', e.target.value)}
                    sx={{ minWidth: 160, flex: 1 }}
                    {...slabFieldProps('lcrr_threshold', row, submitted)}
                  />
                </Box>

                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1.5 }}>
                  <Switch checked={row.is_active} onChange={(e) => updateDraftRow(index, 'is_active', e.target.checked)} />
                  Active
                </Box>
              </Paper>
            ))}

            <Button
              startIcon={<Icon icon="mdi:plus" width={18} />}
              variant="text"
              onClick={addDraftRow}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Another Row
            </Button>

            <EffectiveFromField form={draftEffective} setForm={setDraftEffective} />

            {saveError && <Typography color="error.main" variant="body2">{saveError}</Typography>}

            <Box display="flex" gap={1}>
              <UpdateButton action={handleSaveDrafts} disabled={saving}>
                {saving ? 'Saving...' : draftRows.length > 1 ? 'Save All' : 'Save'}
              </UpdateButton>
              <Button variant="text" onClick={closeForm} disabled={saving}>Cancel</Button>
            </Box>
          </Box>
        </Paper>
      )}

      {rowsLoading && (
        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
          <CircularProgress size={18} />
          <Typography color="text.secondary">Loading slabs...</Typography>
        </Box>
      )}

      {!rowsLoading && rowsError && <RetryNotice message={rowsError} onRetry={fetchRows} />}

      {!rowsLoading && !rowsError && groupedByDepartment.size === 0 && (
        <Typography color="text.secondary">No slabs configured yet. Use "Add Slab" to set one up.</Typography>
      )}

      {!rowsLoading && !rowsError && [...groupedByDepartment.entries()].map(([deptId, deptRows]) => (
        <Box key={deptId} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {departmentName(deptId)}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                  <TableCell><strong>Target Revenue Range</strong></TableCell>
                  <TableCell align="right"><strong>TAR warns below</strong></TableCell>
                  <TableCell align="right"><strong>ARPU warns below</strong></TableCell>
                  <TableCell align="right"><strong>Admission Count warns below</strong></TableCell>
                  <TableCell align="right"><strong>LCRR warns below</strong></TableCell>
                  <TableCell><strong>Applies</strong></TableCell>
                  <TableCell><strong>Active</strong></TableCell>
                  <TableCell><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deptRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatRange(row.target_min, row.target_max)}</TableCell>
                    <TableCell align="right">{row.tar_threshold}%</TableCell>
                    <TableCell align="right">₹{Number(row.arpu_threshold).toLocaleString()}</TableCell>
                    <TableCell align="right">{row.admission_count_threshold}</TableCell>
                    <TableCell align="right">{row.lcrr_threshold}</TableCell>
                    <TableCell><AppliesCell row={row} month={month} /></TableCell>
                    <TableCell>
                      <Switch checked={!!row.is_active} disabled={!!toggling[row.id]} onChange={() => handleToggleActive(row)} />
                    </TableCell>
                    <TableCell>
                      <UpdateButton size="small" action={() => openEdit(row)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </WarningPage>
  );
};

export default TargetSlabConfig;
